/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Star, StarOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLayoutStore, useFavorites, useFavoritesDisplayStyle, FAVORITES_MAX } from '@/stores/layoutStore';

interface FavoritesContextMenuProps {
  href: string;
  x: number;
  y: number;
  onClose: () => void;
  /** Optional override for removal action (e.g. to trigger exit animation before actual removal). */
  onRemove?: () => void;
}

export function FavoritesContextMenu({ href, x, y, onClose, onRemove }: FavoritesContextMenuProps) {
  const { t } = useTranslation('common');
  const favorites = useFavorites();
  const favoritesDisplayStyle = useFavoritesDisplayStyle();
  const addFavorite = useLayoutStore((s) => s.addFavorite);
  const removeFavorite = useLayoutStore((s) => s.removeFavorite);
  const isPinned = favorites.includes(href);
  const isFull = !isPinned && favorites.length >= FAVORITES_MAX[favoritesDisplayStyle];
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click or Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  // Flip left/up if near viewport edges
  const MENU_W = 220;
  const MENU_H = 60;
  const left = x + MENU_W > window.innerWidth - 8 ? x - MENU_W : x;
  const top  = y + MENU_H > window.innerHeight - 8 ? y - MENU_H : y;

  const handleAction = () => {
    if (isPinned) {
      if (onRemove) {
        onRemove();
      } else {
        removeFavorite(href);
        onClose();
      }
    } else if (!isFull) {
      addFavorite(href);
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <>
      <style>{contextMenuStyles}</style>
      <div
        ref={menuRef}
        className="fav-ctx-menu"
        role="menu"
        aria-label={isPinned ? t('favorites.remove') : t('favorites.add')}
        style={{ position: 'fixed', top, left, zIndex: 10000 }}
      >
        <button
          className={`fav-ctx-item${isFull ? ' fav-ctx-item--disabled' : ''}`}
          onClick={handleAction}
          disabled={isFull}
          type="button"
          role="menuitem"
          autoFocus
        >
          {isPinned ? (
            <><StarOff size={14} className="fav-ctx-icon fav-ctx-icon--remove" />{t('favorites.remove')}</>
          ) : (
            <><Star size={14} className="fav-ctx-icon fav-ctx-icon--add" />{t('favorites.add')}</>
          )}
        </button>
        {isFull && (
          <p className="fav-ctx-hint-outer">
            {t('favorites.maxReached', { max: FAVORITES_MAX[favoritesDisplayStyle] })}
          </p>
        )}
      </div>
    </>,
    document.body
  );
}

const contextMenuStyles = `
  .fav-ctx-menu {
    background: rgba(32, 37, 58, 0.92);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(99, 179, 237, 0.15);
    border-radius: 10px;
    padding: 4px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.35);
    animation: fav-ctx-in 0.12s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    min-width: 200px;
  }
  html[data-theme="light"] .fav-ctx-menu {
    background: rgba(255,255,255,0.97);
    border-color: rgba(0,0,0,0.08);
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  }
  @keyframes fav-ctx-in {
    from { opacity: 0; transform: scale(0.93) translateY(-4px); }
    to   { opacity: 1; transform: scale(1)    translateY(0);    }
  }
  .fav-ctx-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    border-radius: 7px;
    border: none;
    background: transparent;
    color: var(--color-text-primary);
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
    text-align: left;
    transition: background 0.12s;
    white-space: nowrap;
  }
  html[data-theme="light"] .fav-ctx-item { color: #1a1a2e; }
  .fav-ctx-item:hover:not(:disabled) {
    background: rgba(99,179,237,0.12);
  }
  .fav-ctx-item--disabled { opacity: 0.5; cursor: not-allowed; }
  .fav-ctx-icon--add    { color: #f6ad55; }
  .fav-ctx-icon--remove { color: #fc8181; }
  .fav-ctx-hint-outer {
    font-size: 10px;
    color: var(--color-text-muted);
    padding: 2px 12px 6px;
    margin: 0;
  }
  html[data-theme="light"] .fav-ctx-hint-outer { color: #94a3b8; }
`;
